/** Pré-carregamento do Mapa em tempo ocioso.
 *
 * O `mapbox-gl` vive num chunk próprio de 537 KB compactados, 1,8 MB depois de
 * descompactado. Sendo `lazy`, ele só começava a ser baixado NO CLIQUE em "Mapa",
 * e a tela ficava no spinner até a rede terminar. Medido no artefato de produção:
 * 1462 ms em banda larga, dos quais 1415 ms são rede pura, e 3482 ms em 4G típico.
 *
 * O trabalho não some — ele sai do caminho crítico. A casca do app sobe, e assim
 * que o navegador fica ocioso o chunk é buscado em segundo plano. Quando o clique
 * vem, o módulo já está no registro e o `lazy` resolve na hora.
 *
 * Deliberadamente NÃO é feito na tela de login: quem não entrou pode nem chegar ao
 * Mapa, e não se gasta a franquia de dados de alguém por especulação.
 */

/** Import ÚNICO do módulo. O `lazy` da rota e o pré-carregamento compartilham esta
 *  promessa, então o chunk é buscado uma vez só, venha o clique antes ou depois. */
let modulo: Promise<typeof import('../../features/mapa/MapaPage')> | null = null

export function importMapaPage() {
  modulo ??= import('../../features/mapa/MapaPage')
  return modulo
}

interface ConexaoDoNavegador {
  saveData?: boolean
  effectiveType?: string
}

/** Rede em que gastar 537 KB por antecipação seria hostil com o usuário. */
function redeDeveEconomizar(): boolean {
  const conexao = (navigator as Navigator & { connection?: ConexaoDoNavegador }).connection
  if (!conexao) return false
  if (conexao.saveData) return true
  return conexao.effectiveType === '2g' || conexao.effectiveType === 'slow-2g'
}

/**
 * Agenda a busca do Mapa para quando o navegador estiver ocioso. Idempotente: uma
 * segunda chamada não dispara nada, porque o import já está memorizado.
 */
export function prefetchMapa(): void {
  if (modulo) return
  if (typeof window === 'undefined') return
  if (redeDeveEconomizar()) return

  const buscar = () => {
    // Falha aqui não é erro do usuário: no clique o `lazy` tenta de novo e é ele
    // quem mostra o estado de carregamento e de erro.
    void importMapaPage().catch(() => {
      modulo = null
    })
  }

  const quandoOcioso = () => {
    const ocioso = window.requestIdleCallback
    if (typeof ocioso === 'function') {
      // O timeout garante que a busca acontece mesmo numa aba que nunca fica ociosa.
      ocioso(buscar, { timeout: 3000 })
    } else {
      window.setTimeout(buscar, 1500)
    }
  }

  // Esperar o `load` antes de agendar. `requestIdleCallback` só olha a main thread,
  // e o que está em disputa aqui é BANDA: 537 KB especulativos concorrendo com as
  // consultas da tela em que o usuário realmente está atrasariam o que ele pediu.
  if (document.readyState === 'complete') {
    quandoOcioso()
  } else {
    window.addEventListener('load', quandoOcioso, { once: true })
  }
}
